import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

/**
 * Public site Footer.
 *
 * Mounted on logged-out marketing / informational pages only. Never rendered
 * inside authenticated dashboards, native app post-auth flows, or screens
 * where chrome would distract (auth, forgot-password, multi-step planner).
 *
 * Two variants:
 *  - default: full 4-column nav for landing / browse / detail / guide pages
 *  - minimal: copyright-only strip for low-chrome surfaces
 */
const Footer = ({ variant = 'default' }) => {
  const navigate = useNavigate();

  if (variant === 'minimal') {
    return (
      <footer className="bg-gray-100 border-t border-gray-200 py-3" data-testid="public-footer-minimal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-500 text-xs">
            © 2026 Celey. Operated by The KRS Group LLC.
          </p>
        </div>
      </footer>
    );
  }

  const linkClass = 'text-gray-400 hover:text-white transition-colors text-sm text-left';

  const go = (path) => () => navigate(path);

  return (
    <footer className="bg-gray-900 text-white pt-10 pb-6 mt-auto" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top: brand + columns */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-400" />
              <span className="text-xl font-bold tracking-tight">Celey</span>
            </div>
            <p className="mt-3 text-xs text-gray-500 leading-relaxed">
              Plan your event. Find your vendors. All in one place.
            </p>
          </div>

          {/* Explore */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-300 mb-3">Explore</h4>
            <ul className="space-y-2">
              <li><button onClick={go('/plan-event')} className={linkClass} data-testid="footer-link-plan-event">Plan an Event</button></li>
              <li><button onClick={go('/browse')} className={linkClass} data-testid="footer-link-browse">Browse Vendors</button></li>
              <li><button onClick={go('/for-vendors')} className={linkClass} data-testid="footer-link-for-vendors">Become a Provider</button></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-300 mb-3">Company</h4>
            <ul className="space-y-2">
              <li><button onClick={go('/about')} className={linkClass} data-testid="footer-link-about">About</button></li>
              <li><button onClick={go('/faqs')} className={linkClass} data-testid="footer-link-faqs">FAQs</button></li>
              <li><button onClick={go('/contact')} className={linkClass} data-testid="footer-link-contact">Contact</button></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-300 mb-3">Legal</h4>
            <ul className="space-y-2">
              <li><button onClick={go('/privacy')} className={linkClass} data-testid="footer-link-privacy">Privacy Policy</button></li>
              <li><button onClick={go('/terms')} className={linkClass} data-testid="footer-link-terms">Terms of Service</button></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-300 mb-3">Support</h4>
            <ul className="space-y-2">
              <li><button onClick={go('/contact')} className={linkClass} data-testid="footer-link-support">Contact Support</button></li>
            </ul>
          </div>
        </div>

        {/* Divider + copyright */}
        <div className="mt-8 pt-6 border-t border-gray-800 text-center md:text-left">
          <p className="text-gray-500 text-xs">
            © 2026 Celey. Operated by The KRS Group LLC. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
